import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { adminApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminWaitlistStats from "./admin/AdminWaitlistStats";
import AdminWaitlistToolbar from "./admin/AdminWaitlistToolbar";
import AdminWaitlistTable from "./admin/AdminWaitlistTable";
import { downloadWaitlistCsv, filterWaitlist } from "./admin/waitlistUtils";

function LockedOut() {
  return (
    <div className="p-10 max-w-xl mx-auto">
      <div className="glass rounded-3xl p-8 text-center">
        <ShieldAlert className="w-10 h-10 text-neon-pink mx-auto mb-3" />
        <div className="font-display text-2xl font-bold mb-2">Admin only</div>
        <p className="text-white/70 text-sm">
          Add your email to <code className="font-mono text-neon-cyan">ADMIN_EMAILS</code> in backend .env to see this page.
        </p>
      </div>
    </div>
  );
}

export default function AdminWaitlist() {
  const { user, loading } = useAuth();
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (loading || !user || !user.is_admin) return;
    adminApi
      .listWaitlist()
      .then(({ data }) => setRows(data))
      .catch(() => setLoadError(true));
  }, [user, loading]);

  const filtered = useMemo(() => filterWaitlist(rows || [], query), [rows, query]);
  const invitedCount = useMemo(
    () => (rows || []).filter((r) => r.invited).length,
    [rows]
  );

  if (loading) return <div className="p-10 text-white/70 font-display">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (loadError || !user.is_admin) return <LockedOut />;

  const handleInvite = async (email) => {
    try {
      await adminApi.invite(email);
      toast.success(`Marked ${email} as invited`);
      setRows((rs) => (rs || []).map((r) => (r.email === email ? { ...r, invited: true } : r)));
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy — long-press to select");
    }
  };

  const handleExport = () => {
    downloadWaitlistCsv(rows || []);
    toast.success("CSV downloaded ✦");
  };

  return (
    <div className="p-8 pb-16">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-neon-cyan mb-2">✦ Admin</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold"><span className="chrome-text">Waitlist</span></h1>
        <p className="text-white/70 mt-2">Founders queued up. Invite when there's room.</p>
      </div>

      <AdminWaitlistStats total={rows === null ? null : rows.length} invited={invitedCount} />

      <div className="glass rounded-3xl p-5">
        <AdminWaitlistToolbar query={query} onQueryChange={setQuery} onExport={handleExport} />
        <AdminWaitlistTable rows={rows} filtered={filtered} onCopy={handleCopy} onInvite={handleInvite} />
      </div>
    </div>
  );
}
